// 📁 src/App.js
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Layout from "./components/Layout";
import AutoRedirect from "./lightversion/AutoRedirect";
import WelcomeHero from "./components/WelcomeHero";

// ---------------- CONTEXTS ----------------
import { IdeaProvider, useIdea } from "./context/IdeaContext";
import { GTMProvider } from "./pages/GTM/context/GTMContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LiteProvider } from "./lightversion/context/LiteContext";
import ProValidation from "./lightversion/ProValidation";

import ProtectedRoute from "./components/ProtectedRoute";

// ---------------- AUTH ----------------
import Login from "./pages/Login";
import Register from "./pages/Register";
import Unauthorized from "./pages/Unauthorized";

// ---------------- PRICING ----------------
import PricingPage from "./pages/pricing/PricingPage";
import Checkout from "./pages/pricing/Checkout";
import PaymentSuccess from "./pages/pricing/PaymentSuccess";
import PaymentFailure from "./pages/pricing/PaymentFailure";

// ---------------- ADMIN ----------------
import AdminUserManagement from "./pages/admin/AdminUserManagement";
import AdminPricingPage from "./pages/pricing/AdminPricingPage";
import BillingDashboard from "./pages/pricing/BillingDashboard";

// ---------------- DASHBOARDS ----------------
import AdminDashboard from "./pages/AdminDashboard";
import InvestorDashboard from "./pages/InvestorDashboard";
import MentorDashboard from "./pages/MentorDashboard";
import MentorPending from "./pages/MentorPending";
import FinalDashboard from "./pages/FinalDashboard";

// ---------------- PRO MODULES ----------------
import IdeaGeneration from "./pages/IdeaGeneration";
import MarketResearch from "./pages/MarketResearch/MarketResearch";
import MVPPlanner from "./pages/MVPPlanner";
import MVPAgent from "./pages/MVPAgent";
import BusinessModelBuilder from "./pages/BusinessModelBuilder";
import AITesting from "./pages/AITesting";
import GTMPlaybook from "./pages/GTM/GTMPlaybook";
import FundingMatch from "./pages/FundingMatch";
import PitchDeckGenerator from "./components/PitchDeckGenerator";
import CommunityProgress from "./pages/CommunityProgress";

// ---------------- FRAMEWORKS ----------------
import SWOTAnalysis from "./pages/MarketResearch/frameworks/SWOTAnalysis";
import TAMAnalysis from "./pages/MarketResearch/frameworks/TAMSamSom";
import LeanCanvas from "./pages/MarketResearch/frameworks/LeanCanvas";
import PorterAnalysis from "./pages/MarketResearch/frameworks/PorterFiveForces";
import JTBDAnalysis from "./pages/MarketResearch/frameworks/JTBD";
import PESTELAnalysis from "./pages/MarketResearch/frameworks/PESTEL";

// ---------------- TRAINING ----------------
import LearningMentorship from "./pages/LearningMentorship";

// ---------------- LITE ----------------
import WelcomeLite from "./lightversion/WelcomeLite";
import PurposeConfirmLite from "./lightversion/PurposeConfirmLite";
import AImentorLite from "./lightversion/AIMentorLite";
import IdeaGeneratorLite from "./lightversion/IdeaGeneratorLite";
import MarketPotentialLite from "./lightversion/MarketPotentialLite";
import LearningLite from "./lightversion/LearningLite";
import UpgradePopup from "./lightversion/UpgradePopup";
import RegistrationGateLite from "./lightversion/RegistrationGateLite";
import DirectionConfirmLite from "./lightversion/DirectionConfirmLite";
import Day7Lite from "./lightversion/Day7Lite";
import ProComingSoonLite from "./lightversion/ProComingSoonLite";

// ---------------- Terms and Privacy ----------------
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import { trackUserRole } from "./utils/analytics";

// ---------------- USER SYNC ----------------
const UserSync = () => {
  const { user } = useAuth();
  const { userId, setUserId } = useIdea();

  useEffect(() => {
    if (user && (user._id || user.id)) {
      const id = user._id || user.id;
      if (userId !== id) setUserId(id);
    }
  }, [user, userId, setUserId]);

  return null;
};

// ---------------- ANALYTICS TRACKER ----------------


const AnalyticsTracker = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const role = user?.role || "Guest";

    localStorage.setItem("user_role", role);

    trackUserRole(role);
  }, [location, user]);

  return null;
};

const App = () => {
  return (
    <AuthProvider>
      <IdeaProvider>
        
          <LiteProvider>
            <Router>
              <UserSync />
              <AnalyticsTracker />
              <Routes>
                {/* ================= ROOT ================= */}
                <Route path="/" element={<AutoRedirect />} />

                {/* ================= LITE (NO LAYOUT) ================= */}
                <Route path="/lite" element={<Navigate to="/lite/welcome" replace />} />

                <Route path="/lite/welcome" element={<WelcomeLite />} />
                <Route path="/lite/mentor" element={<AImentorLite />} />
                <Route path="/lite/purpose" element={<PurposeConfirmLite />} />
                <Route path="/lite/idea" element={<IdeaGeneratorLite />} />
                <Route path="/lite/direction-confirm" element={<DirectionConfirmLite />} />
                <Route path="/lite/registration-gate" element={<RegistrationGateLite />} />
                <Route path="/lite/market-potential" element={<MarketPotentialLite />} />
                <Route path="/lite/learning" element={<LearningLite />} />
                <Route path="/lite/day-7" element={<Day7Lite />} />
                <Route path="/lite/upgrade" element={<UpgradePopup />} />
                <Route path="/lite/pro-coming-soon" element={<ProComingSoonLite />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />

                {/* ================= AUTH ================= */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route
                  path="/pro/validation"
                  element={
                    <ProtectedRoute>
                      <ProValidation />
                    </ProtectedRoute>
                  }
/>

                {/* ================= PRICING ================= */}

                <Route
                  path="/pricing"
                  element={
                    <ProtectedRoute>
                      <PricingPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/pricing/checkout"
                  element={
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/pricing/success"
                  element={
                    <ProtectedRoute>
                      <PaymentSuccess />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/pricing/failure"
                  element={
                    <ProtectedRoute>
                      <PaymentFailure />
                    </ProtectedRoute>
                  }
                />
                
                {/* ================= MAIN APP (WITH LAYOUT) ================= */}
                <Route element={<Layout />}>
                  {/* ---------- PRO ---------- */}
                  <Route
                    path="/pro"
                    element={
                      <ProtectedRoute requiresPro>
                        <WelcomeHero />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute requiresPro>
                        <FinalDashboard />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/idea-generation"
                    element={
                      <ProtectedRoute requiresPro>
                        <IdeaGeneration />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/market-research"
                    element={
                      <ProtectedRoute requiresPro>
                        <MarketResearch />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/market-research/swot"
                    element={
                      <ProtectedRoute requiresPro>
                        <SWOTAnalysis />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/market-research/tam-sam-som"
                    element={
                      <ProtectedRoute requiresPro>
                        <TAMAnalysis />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/market-research/lean-canvas"
                    element={
                      <ProtectedRoute requiresPro>
                        <LeanCanvas />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/market-research/porter"
                    element={
                      <ProtectedRoute requiresPro>
                        <PorterAnalysis />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/market-research/jtbd"
                    element={
                      <ProtectedRoute requiresPro>
                        <JTBDAnalysis />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/market-research/pestel"
                    element={
                      <ProtectedRoute requiresPro>
                        <PESTELAnalysis />
                      </ProtectedRoute>
                    }
                  />    

                  <Route
                    path="/mvp-planner"
                    element={
                      <ProtectedRoute requiresPro>
                        <MVPPlanner />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/mvp-agent"
                    element={
                      <ProtectedRoute requiresPro>
                        <MVPAgent />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/business-model-builder"
                    element={
                      <ProtectedRoute requiresPro>
                        <BusinessModelBuilder />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/ai-testing"
                    element={
                      <ProtectedRoute requiresPro>
                        <AITesting />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/gtm-accelerator"
                    element={
                      <ProtectedRoute requiresPro>
                        <GTMProvider>
                           <GTMPlaybook />
                        </GTMProvider>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/funding-match"
                    element={
                      <ProtectedRoute requiresPro>
                        <FundingMatch />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/pitch-deck"
                    element={
                      <ProtectedRoute requiresPro>
                        <PitchDeckGenerator />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                  path="/community-progress"
                  element={
                  <ProtectedRoute requiresPro>
                  <CommunityProgress />
                  </ProtectedRoute>
                  }
                  />

                  {/* ---------- TRAINING ---------- */}
                  <Route
                    path="/training"
                    element={
                      <ProtectedRoute>
                        <LearningMentorship />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/training/:slug"
                    element={
                      <ProtectedRoute>
                        <LearningMentorship />
                      </ProtectedRoute>
                    }
                  />

                    <Route
                      path="/learning-mentorship"
                      element={
                        <ProtectedRoute>
                          <LearningMentorship />
                        </ProtectedRoute>
                    }
                  />

                  {/* ---------- ADMIN ---------- */}
                  <Route
                    path="/admin-dashboard"
                    element={
                      <ProtectedRoute role="admin">
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute role="admin">
                        <AdminUserManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/pricing"
                    element={
                      <ProtectedRoute role="admin">
                        <AdminPricingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/billing"
                    element={
                      <ProtectedRoute role="admin">
                        <BillingDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* ---------- INVESTOR ---------- */}
                  <Route
                    path="/investor-dashboard"
                    element={
                      <ProtectedRoute role="investor">
                        <InvestorDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* ---------- MENTOR ---------- */}
                  <Route
                    path="/mentor-dashboard"
                    element={
                      <ProtectedRoute role="mentor">
                        <MentorDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mentor-pending"
                    element={
                      <ProtectedRoute role="mentor">
                        <MentorPending />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* ================= 404 ================= */}
                <Route
                  path="*"
                  element={
                    <div style={{ padding: 40, textAlign: "center", color: "red" }}>
                      404 – Page Not Found
                    </div>
                  }
                />
              </Routes>
            </Router>
          </LiteProvider>
        </IdeaProvider>
    </AuthProvider>
  );
};

export default App;
